import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AppsOutlined, AppsRounded, Close, PolicyOutlined} from "@mui/icons-material";
import {Button, Container, Popover, Typography, Unstable_Grid2 as Grid, useMediaQuery} from "@mui/material";
import debounce from "lodash/debounce";

// @ts-ignore
import BizpireImage from '../../../../../assets/logos/bizpire.png';
// @ts-ignore
import BizCmsImage from '../../../../../assets/logos/bizcms.png';
// @ts-ignore
import BizQrcodeImage from '../../../../../assets/logos/bizQrcode.png';
// @ts-ignore
import BizCrmImage from '../../../../../assets/logos/bizCRM.png';
// @ts-ignore
import BizLearnImage from '../../../../../assets/logos/bizLearn.png';
// @ts-ignore
import BizCoreImage from '../../../../../assets/logos/bizCore.png';
// @ts-ignore
import BizHrImage from '../../../../../assets/logos/bizHr.png';
// @ts-ignore
import BizProductionImage from '../../../../../assets/logos/bizProduction.png';
import EnvService from "../../../../../core/services/env";
import PartnerIndustryRules from "../../../../../core/services/partner-industry-rules";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {usePartnerInformation} from "../../../../hooks/use-partner-information";
import {useTranslation} from "react-i18next";
import Utils from "../../../../../core/services/utils/utils";


const _urls = {
    bizCore: EnvService.ApplicationUrls.bizCore,
    bizCrm: EnvService.ApplicationUrls.bizCrm,
    bizCms: EnvService.ApplicationUrls.bizCms,
    bizInventory: EnvService.ApplicationUrls.bizInventory,
    bizLearn: EnvService.ApplicationUrls.bizLearn,
    bizQrCode: EnvService.ApplicationUrls.bizQrCode,
    bizHr: EnvService.ApplicationUrls.bizHr,
    bizProduction: EnvService.ApplicationUrls.bizProduction,
    qmsPanel: 'https://sites.google.com/chtp.ca/qms-chtp/controlled-documents?authuser=0',
}


/**
 * The applications that operator can access
 */
const _connectedApps = (t) => [
    {
        name: t('biz-core'),
        url: _urls.bizCore,
        logoUrl: BizCoreImage,
        description: t('biz-core-description'),
    },
    {
        name: t('biz-crm'),
        url: _urls.bizCrm,
        logoUrl: BizCrmImage,
        description: t('biz-crm-description'),
    },
    {
        name: t('biz-cms'),
        url: _urls.bizCms,
        logoUrl: BizCmsImage,
        description: t('biz-cms-description'),
    },
    {
        name: t('biz-inventory'),
        url: _urls.bizInventory,
        logoUrl: BizpireImage,
        description: t('biz-inventory-description'),
    },
    {
        name: t('biz-learn'),
        url: _urls.bizLearn,
        logoUrl: BizLearnImage,
        description: t('biz-learn-description'),
    },
    {
        name: t('biz-qrcode'),
        url: _urls.bizQrCode,
        logoUrl: BizQrcodeImage,
        description: t('biz-qrcode-description'),
    },
    {
        name: t('biz-hr'),
        url: _urls.bizHr,
        logoUrl: BizHrImage,
        description: t('biz-hr-description'),
    },
    {
        name: t('biz-production'),
        url: _urls.bizProduction,
        logoUrl: BizProductionImage,
        description: t('biz-production-description'),
    },
    {
        name: t('qms-panel'),
        url: _urls.qmsPanel,
        icon: PolicyOutlined,
        description: t('qms-panel-description'),
    },
]

const useConnectedAppsEndAction = (breakpoint) => {
    const [popoverAnchor, setPopoverAnchor] = useState();
    const passedBreakpoint = useMediaQuery((theme) => theme.breakpoints.up(breakpoint));
    const popoverAction = useRef(null);
    const operatorAccessLevels = useAccessLevels();
    const partnerInformation = usePartnerInformation();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.connected-apps'});

    const connectedApps = useMemo(() => {
        const allApps = [..._connectedApps(t)];
        if (!operatorAccessLevels.packages.base.connectedAppsBizCore) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizCore), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizCrm) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizCrm), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizCms) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizCms), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizHr) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizHr), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizInventory) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizInventory), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizLearn) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizLearn), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizQrCode) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizQrCode), 1);
        }
        if (!operatorAccessLevels.packages.base.connectedAppsBizProduction) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.bizProduction), 1);
        }
        if (!PartnerIndustryRules.showQmsPanelApp(partnerInformation?.id)) {
            allApps.splice(allApps.findIndex(e => e.url === _urls.qmsPanel), 1);
        }
        return allApps;
    }, [operatorAccessLevels, partnerInformation?.id, t])
    const noAppsAvailable = useMemo(() => connectedApps.length === 0 || !operatorAccessLevels.packages.base.connectedApps, [connectedApps, operatorAccessLevels]);

    /**
     * With each change in the [passedBreakpoint] value:
     * - Close the popover
     */
    useEffect(() => {
        setPopoverAnchor(undefined)
    }, [passedBreakpoint])

    /**
     * Update the popover position on window resize.
     */
    useEffect(() => {
        if (!popoverAnchor)
            return;
        const debounced = debounce(() => {
            popoverAction.current?.updatePosition()
        }, 1000)
        window.addEventListener('resize', debounced);
        return () => window.removeEventListener('resize', debounced);
    }, [popoverAnchor])

    /**
     * Open the plugins popover
     */
    const openPopover = useCallback((e, anchorElement) => {
        setPopoverAnchor(anchorElement ?? e.currentTarget);
    }, [])

    /**
     * Close the plugins popover
     */
    const closePopover = useCallback(() => {
        setPopoverAnchor(undefined)
    }, [])


    const pluginsPopover = useMemo(() => (
        <Popover
            id={'connected-apps-popover'}
            action={popoverAction}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            slotProps={{root: {sx: {marginBlockStart: 2}}, paper: {sx: (theme) => ({boxShadow: theme.shadows[2]})}}}
            open={!!popoverAnchor}
            anchorEl={popoverAnchor}
            onClose={closePopover}
        >
            <ConnectedApps connectedApps={connectedApps}/>
        </Popover>
    ), [popoverAnchor, closePopover, connectedApps]);

    return useMemo(() =>
            noAppsAvailable
                ? null
                : ({
                    key: "connected-apps",
                    title: t('title'),
                    icon: popoverAnchor ? <AppsRounded/> : <AppsOutlined/>,
                    color: "primary",
                    onClick: openPopover,
                    children: pluginsPopover,
                }),
        [noAppsAvailable, popoverAnchor, openPopover, pluginsPopover, t])
}

const ConnectedApps = ({connectedApps}) => {
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.connected-apps'});

    return (
        <Container
            maxWidth={'xs'}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
                gap: 2,
            }}
        >
            <Typography variant={'body1'} fontWeight={600}>
                {t('title')}
            </Typography>
            <Grid container spacing={2}>
                {
                    connectedApps.map((app) => (
                        <Grid xs={12}>
                            <Button
                                variant={'text'}
                                color={'inherit'}
                                key={app.name}
                                className='w-100 d-flex justify-content-start align-items-center'
                                component={'a'}
                                href={app.url}
                                target={'_blank'}
                            >
                                <div className='image apps'>
                                    {
                                        app.logoUrl
                                            ? <img src={Utils.encodeFileUrl(app.logoUrl)} alt={app.name + ' logo'}/>
                                            : app.icon
                                                ? <app.icon className={'icon large'} color={'primary'}/>
                                                : <Close className={'icon large white'}/>
                                    }
                                </div>
                                <div className={'d-flex flex-column align-items-start pl-3'}>
                                    <p className='text-md font-weight-semi-bold text-left'>
                                        {app.name}
                                    </p>
                                    <p className='text-xs text-left font-weight-normal'>
                                        {app.description ?? ''}
                                    </p>
                                </div>
                            </Button>
                        </Grid>
                    ))
                }
            </Grid>
        </Container>
    );
}

export default useConnectedAppsEndAction;
